/**
 * history.ts — Firestore helpers for chat & debate session persistence.
 *
 * Data model (all scoped under users/{uid}):
 *   users/{uid}/chatSessions/{sessionId}
 *   users/{uid}/debateSessions/{sessionId}
 */

import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
  writeBatch,
  Timestamp,
  DocumentReference,
} from "firebase/firestore";
import { db } from "./config";
import { GeneratedMediaItem } from "../types/media";
import { AnswerFormat, Lean, Verdict } from "../utils/verdict";
import { DebateSource } from "../utils/sources";

// ─── Shared types ─────────────────────────────────────────────────────────────

export interface StoredMessage {
  id: string;
  text: string;
  sender: "user" | "ai";
  timestamp: number; // ms since epoch — plain number, safe for Firestore
  model?: string;
  media?: GeneratedMediaItem[];
}

export interface StoredDebateStep {
  expert: string;
  step: string;
  round?: number;
  answer?: string;
  feedback?: string;
  revised?: string;
  done?: boolean;
  /** Feedback split by the expert it addresses; absent on debates saved before threading. */
  targets?: Record<string, string>;
  toPanel?: string;
  /** The expert's stated lean on a question with fixed answers. */
  lean?: Lean;
  /** Ids from the session's sources. */
  sourceIds?: string[];
}

export interface StoredChaosStep {
  round: number;
  chaos: string;
  model?: string;
  done?: boolean;
  /** Attack split by the expert it targets; absent on debates saved before threading. */
  targets?: Record<string, string>;
  toPanel?: string;
  sourceIds?: string[];
}

// ─── Chat session ──────────────────────────────────────────────────────────────

export interface ChatSession {
  id: string;
  title: string;
  model: string;
  messages: StoredMessage[];
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

/** Creates a new empty chat session and returns its Firestore ref. */
export async function createChatSession(
  uid: string,
  firstMessage: string,
  model: string,
): Promise<DocumentReference> {
  const colRef = collection(db, "users", uid, "chatSessions");
  return addDoc(colRef, {
    title: firstMessage.slice(0, 80),
    model,
    messages: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/** Overwrites the messages array and bumps updatedAt. */
export async function saveChatMessages(
  uid: string,
  sessionId: string,
  messages: StoredMessage[],
  model: string,
): Promise<void> {
  const ref = doc(db, "users", uid, "chatSessions", sessionId);
  await updateDoc(ref, {
    messages,
    model,
    updatedAt: serverTimestamp(),
  });
}

/** Loads all chat sessions for a user, newest first. */
export async function loadChatSessions(uid: string): Promise<ChatSession[]> {
  const colRef = collection(db, "users", uid, "chatSessions");
  const q = query(colRef, orderBy("updatedAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ChatSession);
}

/** Deletes a chat session. */
export async function deleteChatSession(
  uid: string,
  sessionId: string,
): Promise<void> {
  await deleteDoc(doc(db, "users", uid, "chatSessions", sessionId));
}

/** Deletes multiple chat sessions in a batch. */
export async function deleteChatSessionsBatch(
  uid: string,
  sessionIds: string[],
): Promise<void> {
  const batch = writeBatch(db);
  for (const id of sessionIds) {
    batch.delete(doc(db, "users", uid, "chatSessions", id));
  }
  await batch.commit();
}

// ─── Debate session ────────────────────────────────────────────────────────────

export interface DebateSession {
  id: string;
  topic: string;
  experts: string[];
  rounds: number;
  plan: string;
  model: string;
  debateMode?: "single" | "multi";
  /** The backend debate doc; absent on sessions saved before the feed existed. */
  debateId?: string;
  councilProgress: StoredDebateStep[];
  chaosProgress: StoredChaosStep[];
  councilFinal: string | null;
  /** Absent on sessions saved before verdicts existed and on open-ended questions. */
  verdict?: Verdict | null;
  answerFormat?: AnswerFormat | null;
  /** Absent on sessions saved before sources existed. */
  sources?: DebateSource[];
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

/** Creates a new debate session skeleton as soon as the user submits. */
export async function createDebateSession(
  uid: string,
  topic: string,
  experts: string[],
  rounds: number,
  plan: string,
  model: string,
  debateMode: "single" | "multi" = "multi",
): Promise<DocumentReference> {
  const colRef = collection(db, "users", uid, "debateSessions");
  return addDoc(colRef, {
    topic: topic.slice(0, 200),
    experts,
    rounds,
    plan,
    model,
    debateMode,
    councilProgress: [],
    chaosProgress: [],
    councilFinal: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/** Links a session to its backend debate so it can be posted to the feed later. */
export async function attachDebateId(
  uid: string,
  sessionId: string,
  debateId: string,
): Promise<void> {
  await updateDoc(doc(db, "users", uid, "debateSessions", sessionId), { debateId });
}

/** Saves the final debate state (called once the facilitator is done). */
export async function saveDebateSession(
  uid: string,
  sessionId: string,
  councilProgress: StoredDebateStep[],
  chaosProgress: StoredChaosStep[],
  councilFinal: string | null,
  outcome: { verdict?: Verdict | null; answerFormat?: AnswerFormat | null; sources?: DebateSource[] } = {},
  /** Repairing an old item from history shouldn't move it to the top of the list. */
  { keepPosition = false }: { keepPosition?: boolean } = {},
): Promise<void> {
  const ref = doc(db, "users", uid, "debateSessions", sessionId);
  await updateDoc(ref, {
    councilProgress,
    chaosProgress,
    councilFinal,
    verdict: outcome.verdict ?? null,
    answerFormat: outcome.answerFormat ?? null,
    sources: outcome.sources ?? [],
    ...(keepPosition ? {} : { updatedAt: serverTimestamp() }),
  });
}

/** Loads all debate sessions for a user, newest first. */
export async function loadDebateSessions(
  uid: string,
): Promise<DebateSession[]> {
  const colRef = collection(db, "users", uid, "debateSessions");
  const q = query(colRef, orderBy("updatedAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as DebateSession);
}

/** Deletes a debate session. */
export async function deleteDebateSession(
  uid: string,
  sessionId: string,
): Promise<void> {
  await deleteDoc(doc(db, "users", uid, "debateSessions", sessionId));
}

/** Deletes multiple debate sessions in a batch. */
export async function deleteDebateSessionsBatch(
  uid: string,
  sessionIds: string[],
): Promise<void> {
  const batch = writeBatch(db);
  for (const id of sessionIds) {
    batch.delete(doc(db, "users", uid, "debateSessions", id));
  }
  await batch.commit();
}
